import { Autocomplete, AutocompleteProps, AutocompleteValue, TextField, TextFieldProps } from '@mui/material';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

export type AutocompleteControlProps<
  TFieldValues extends FieldValues,
  TOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  id?: string;
  textFieldProps?: TextFieldProps;
  rules?: Omit<RegisterOptions<TFieldValues, Path<TFieldValues>>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
} & Omit<AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'options' | 'onChange' | 'value' | 'name'> & {
    options: AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>['options'];
    renderInput?: AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>['renderInput'];
  };

export function AutocompleteControl<
  TFieldValues extends FieldValues,
  TOption,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  name,
  control,
  rules,
  id,
  textFieldProps,
  options,
  renderInput,
  onInputChange,
  ...autocompleteProps
}: AutocompleteControlProps<TFieldValues, TOption, Multiple, DisableClearable, FreeSolo>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, value: fieldValue, ref } = field;

        let value: AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;

        if (autocompleteProps.multiple) {
          value = (fieldValue ?? []) as AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;
        } else {
          value = (fieldValue ?? null) as AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>;
        }

        const { slotProps: textFieldSlotProps, ...restTextFieldProps } = textFieldProps ?? {};

        return (
          <Autocomplete
            {...autocompleteProps}
            options={options}
            value={value}
            onBlur={onBlur}
            onChange={(_, newValue) => onChange(newValue)}
            onInputChange={(event, newValue, reason) => {
              onInputChange?.(event, newValue, reason);
              if (autocompleteProps.freeSolo && reason === 'input') {
                onChange(newValue);
              }
            }}
            renderInput={
              renderInput ??
              (({ slotProps: inputSlotProps, ...params }) => (
                <TextField
                  {...params}
                  {...restTextFieldProps}
                  {...(id != null ? { id } : {})}
                  inputRef={ref}
                  variant="outlined"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  slotProps={{
                    ...textFieldSlotProps,
                    input: {
                      ...inputSlotProps.input,
                      ...textFieldSlotProps?.input,
                    },
                    htmlInput: {
                      ...inputSlotProps.htmlInput,
                      ...textFieldSlotProps?.htmlInput,
                      name: String(name),
                    },
                    inputLabel: {
                      ...inputSlotProps.inputLabel,
                      ...textFieldSlotProps?.inputLabel,
                    },
                  }}
                />
              ))
            }
          />
        );
      }}
    />
  );
}
